import React, { useState } from 'react';
import { Modal, Icon, Switch, Tooltip } from '@trellixio/roaster-coffee';
import PropTypes from 'prop-types';
import Locales from 'locales';
import { formatNumber } from 'helpers';
import styles from './Trips.module.css';

const CONST_STANDARD = 'Standard';
const CONST_PREMIUM = 'Premium';
const CONST_ELITE = 'Elite';
const CONST_DIASPORA_STANDARD = 'Diaspora Standard';
const CONST_DIASPORA_PREMIUM = 'Diaspora Premium';
const CONST_DIASPORA_MAX_SEATS = 200;

const CategoryCard = ({ name, bgColor, advantages, adultAmount, childAmount, availableSeats, diasporaSelected }) => {
  return (
    <article className={styles.categoryCard}>
      <div className={`${styles.categoryCardHeader} ${bgColor}`}>
        <p>{name}</p>

        <div className={styles.advantages}>
          <ul>
            {advantages.map((advantage) => (
              <li>{advantage}</li>
            ))}
          </ul>
        </div>

        {!diasporaSelected && (
          <div>
            {Locales.trip.categories.available_seats}
            <strong>{formatNumber(availableSeats)}</strong>
          </div>
        )}
      </div>

      <div className={styles.categoryCardContent}>
        <div>
          <p>{Locales.trip.categories.labels.adult}</p>
          <strong>
            {`${formatNumber(adultAmount)} `}
            <span className={styles.noBold}>FCFA</span>
          </strong>
        </div>

        <div>
          <p>{Locales.trip.categories.labels.child}</p>
          <strong>
            {`${formatNumber(childAmount)} `}
            <span className={styles.noBold}>FCFA</span>
          </strong>
        </div>
      </div>
    </article>
  );
};

CategoryCard.defaultProps = {
  name: null,
  bgColor: null,
  advantages: null,
  adultAmount: null,
  childAmount: null,
  availableSeats: null,
  diasporaSelected: false,
};

CategoryCard.propTypes = {
  name: PropTypes.string,
  bgColor: PropTypes.string,
  advantages: PropTypes.arrayOf(PropTypes.string),
  adultAmount: PropTypes.number,
  childAmount: PropTypes.number,
  availableSeats: PropTypes.number,
  diasporaSelected: PropTypes.bool,
};

const TripCategoriesModal = ({ isOpened, closeModal, categoriesData }) => {
  const [diasporaSelected, setDiasporaSelected] = useState(false);

  const getCategoryDetails = (name) => {
    const category = categoriesData.find((cat) => cat.name === name);
    return category
      ? {
          adultAmount: category.price.adult,
          childAmount: category.price.child,
          occupiedSeats: category.occupied_seats,
          maxSeats: category.max_seats,
        }
      : {};
  };

  const standardDetails = getCategoryDetails(CONST_STANDARD);
  const premiumDetails = getCategoryDetails(CONST_PREMIUM);
  const eliteDetails = getCategoryDetails(CONST_ELITE);
  const diasporaStandardDetails = getCategoryDetails(CONST_DIASPORA_STANDARD);
  const diasporaPremiumDetails = getCategoryDetails(CONST_DIASPORA_PREMIUM);

  const getAvailableSeats = (maxSeats, occupiedSeats) => Math.max(maxSeats - occupiedSeats, 0);

  const standardAvailableSeats = getAvailableSeats(standardDetails.maxSeats, standardDetails.occupiedSeats);
  const premiumAvailableSeats = getAvailableSeats(premiumDetails.maxSeats, premiumDetails.occupiedSeats);
  const eliteAvailableSeats = getAvailableSeats(eliteDetails.maxSeats, eliteDetails.occupiedSeats);
  const diasporaAvailableSeats = getAvailableSeats(
    CONST_DIASPORA_MAX_SEATS - (diasporaStandardDetails.occupiedSeats + diasporaPremiumDetails.occupiedSeats),
    0
  );

  const categories = [
    {
      name: Locales.trip.categories.standard,
      bgColor: styles.headerStandard,
      adultAmount: diasporaSelected ? diasporaStandardDetails.adultAmount : standardDetails.adultAmount,
      childAmount: diasporaSelected ? diasporaStandardDetails.childAmount : standardDetails.childAmount,
      availableSeats: standardAvailableSeats,
      advantages: [
        Locales.trip.categories.advantages.transport_standard,
        Locales.trip.categories.advantages.lodging_standard,
        Locales.trip.categories.advantages.services_standard,
      ],
    },
    {
      name: Locales.trip.categories.premium,
      bgColor: styles.headerPremium,
      adultAmount: diasporaSelected ? diasporaPremiumDetails.adultAmount : premiumDetails.adultAmount,
      childAmount: diasporaSelected ? diasporaPremiumDetails.childAmount : premiumDetails.childAmount,
      availableSeats: premiumAvailableSeats,
      advantages: [
        Locales.trip.categories.advantages.transport_premium,
        Locales.trip.categories.advantages.lodging_premium,
        Locales.trip.categories.advantages.services_premium,
      ],
    },
    {
      name: Locales.trip.categories.elite,
      bgColor: styles.headerElite,
      adultAmount: eliteDetails.adultAmount,
      childAmount: eliteDetails.childAmount,
      availableSeats: eliteAvailableSeats,
      advantages: [
        Locales.trip.categories.advantages.transport_premium,
        Locales.trip.categories.advantages.lodging_elite,
        Locales.trip.categories.advantages.services_elite,
      ],
    },
  ];

  const displayedCategories = diasporaSelected
    ? categories.filter((category) => category.name !== Locales.trip.categories.elite)
    : categories;

  return (
    <Modal title={Locales.trip.categories.title} opened={isOpened} onClose={closeModal} size="large">
      <div className={styles.categoriesHeader}>
        <div className={styles.categoriesHeader}>
          <span>{Locales.trip.categories.diaspora}</span>

          <form>
            <Switch onChange={setDiasporaSelected} />
          </form>
        </div>

        {diasporaSelected && (
          <div className={styles.categoriesHeader}>
            <Tooltip position="right" label={Locales.trip.categories.diaspora_shared_seats_note}>
              <Icon small>
                <i className="fa-regular fa-circle-info" />
              </Icon>
            </Tooltip>
            <span>{`${Locales.trip.categories.available_seats} `}</span>
            <strong>{formatNumber(diasporaAvailableSeats)}</strong>
          </div>
        )}
      </div>

      <div className={styles.categoryCardContainer}>
        {displayedCategories.map((category) => (
          <CategoryCard
            name={category.name}
            bgColor={category.bgColor}
            advantages={category.advantages}
            adultAmount={category.adultAmount}
            childAmount={category.childAmount}
            availableSeats={category.availableSeats}
            diasporaSelected={diasporaSelected}
          />
        ))}
      </div>
    </Modal>
  );
};

TripCategoriesModal.defaultProps = {
  isOpened: false,
  closeModal: null,
  categoriesData: null,
};

TripCategoriesModal.propTypes = {
  isOpened: PropTypes.bool,
  closeModal: PropTypes.func,
  categoriesData: PropTypes.arrayOf(PropTypes.shape({})),
};

export default TripCategoriesModal;
